/* ============================================
   SOUND MAPPING - Correspondance événements → sons
   ============================================
   Mapping complet : fichiers, événements, états Layer 2,
   cooldowns, sound stacks, spatialisation.
   ============================================ */

/**
 * Détection du format audio optimal.
 * Opus/WebM = meilleure qualité à débit égal, supporté partout sauf vieux Safari.
 * MP3 = fallback universel.
 */
const _supportsOpus = (() => {
  try {
    const a = document.createElement('audio')
    return a.canPlayType('audio/webm; codecs=opus') === 'probably'
  } catch {
    return false
  }
})()

const _ext = _supportsOpus ? '.webm' : '.mp3'

/**
 * Fichiers audio à pré-charger.
 * Les chemins sont absolus (relatifs à la racine du serveur web).
 * Les fichiers manquants sont ignorés silencieusement au chargement.
 */
export const SOUND_FILES = {
  // Layer 1 — Ambiance continue
  ambiance:            `/audio/stadium/possession${_ext}`,
  ambiance2ndHalf:     `/audio/stadium/ambiance-2mt${_ext}`,
  ambianceExtraTime:   `/audio/stadium/ambiance-prolongation${_ext}`,
  shootoutAmbiance:    `/audio/stadium/shootout-ambiance${_ext}`,

  // Layer 2 — États conditionnels (loops)
  dangerousAttack:     `/audio/stadium/attaquedangereuse${_ext}`,

  // Layer 3 — One-shots
  goal:                `/audio/stadium/but${_ext}`,
  shotOnTarget:        `/audio/stadium/frappecadré${_ext}`,
  shotOffTarget:       `/audio/stadium/tirsnoncadré${_ext}`,
  yellowCard:          `/audio/stadium/jaune${_ext}`,
  redCard:             `/audio/stadium/cartonrouge${_ext}`,  // fallback: jaune si absent
  whistleShort:        `/audio/stadium/sifflet-court${_ext}`,
  whistleDouble:       `/audio/stadium/sifflet-double${_ext}`,
  whistleTriple:       `/audio/stadium/sifflet-triple${_ext}`,
  crowdOhh:            `/audio/stadium/crowd-ohh${_ext}`,
  crowdGasp:           `/audio/stadium/crowd-gasp${_ext}`,
  crowdApplause:       `/audio/stadium/crowd-applause${_ext}`,
  crowdBoo:            `/audio/stadium/crowd-boo${_ext}`,
  varStart:            `/audio/stadium/var-start${_ext}`,
  penaltyAwarded:      `/audio/stadium/penalty-awarded${_ext}`,
  penaltyMissed:       `/audio/stadium/penalty-missed${_ext}`,
  shootoutScored:      `/audio/stadium/shootout-scored${_ext}`,
  shootoutMissed:      `/audio/stadium/shootout-missed${_ext}`,
  substitutionApplause:`/audio/stadium/substitution-applause${_ext}`,
  injuryConcern:       `/audio/stadium/blesse${_ext}`,
  crowdExodus:         `/audio/stadium/crowd-exodus${_ext}`,

  // Layer 4 — Chants de fond (~30s, joués une fois puis silence aléatoire)
  chant1:              `/audio/stadium/chant-1${_ext}`,
  chant2:              `/audio/stadium/chant-2${_ext}`,
  chant3:              `/audio/stadium/chant-3${_ext}`,
}

/**
 * Configuration Layer 4 — Chants de fond.
 * Le moteur pioche aléatoirement parmi les chants disponibles.
 */
export const LAYER4_CONFIG = {
  /** Clés des chants dans SOUND_FILES (le moteur filtre ceux qui sont chargés) */
  chantKeys: ['chant1', 'chant2', 'chant3'],

  /** Volume de Layer 4 (bas pour rester en fond) */
  volume: 0.25,

  /** Intervalle entre deux chants : [min, max] en secondes */
  interval: [45, 90],

  /** Fade-in / fade-out d'un chant (en secondes) */
  fadeIn: 3.0,
  fadeOut: 3.0,

  /** États Layer 2 pendant lesquels les chants sont BLOQUÉS (pas de nouveau chant + fadeOut si en cours) */
  blockedByStates: ['var', 'penalty'],

  /** Événements qui coupent immédiatement un chant en cours */
  killEvents: ['Mi-temps', 'Mi-temps prolongation', 'Fin du match', 'Fin de prolongation'],
}

/**
 * Correspondance type d'événement radar → son à jouer.
 *
 * layer 2 = état conditionnel (crossfade loop, géré par la machine d'états)
 * layer 3 = one-shot (joué une seule fois puis s'arrête)
 */
export const EVENT_SOUND_MAP = {
  // --- Layer 2 : états conditionnels ---
  'Attaque dangereuse':             { key: 'dangerousAttack',       layer: 2 },
  'VAR':                            { key: 'dangerousAttack',       layer: 2 },
  "VAR (vérification d'un but)":    { key: 'dangerousAttack',       layer: 2 },
  'VAR (vérification penalty)':     { key: 'dangerousAttack',       layer: 2 },
  'VAR (Carton rouge)':             { key: 'dangerousAttack',       layer: 2 },
  'Corner':                         { key: 'dangerousAttack',       layer: 2 },
  'Coup franc dangereux':           { key: 'dangerousAttack',       layer: 2 },
  'Temps additionnel':              { key: 'dangerousAttack',       layer: 2 },

  // --- Layer 3 : événements ponctuels ---
  'But':                            { key: 'goal',                  layer: 3 },
  'Frappe cadrée':                  { key: 'shotOnTarget',          layer: 3 },
  'Frappe non cadrée':              { key: 'shotOffTarget',         layer: 3 },
  'Tir non cadré':                  { key: 'shotOffTarget',         layer: 3 },
  'Carton jaune':                   { key: 'yellowCard',            layer: 3 },
  'Carton rouge':                   { key: 'redCard',               layer: 3 },
  'Penalty':                        { key: 'penaltyAwarded',        layer: 3 },
  'Penalty manqué':                 { key: 'crowdOhh',              layer: 3 },
  "Coup d'envoi":                   { key: 'whistleShort',          layer: 3 },
  "Coup d'envoi 2ème mi-temps":     { key: 'whistleShort',          layer: 3 },
  "Coup d'envoi prolongation":      { key: 'whistleShort',          layer: 3 },
  "Coup d'envoi 2ème MT prolongation": { key: 'whistleShort',       layer: 3 },
  'Mi-temps':                       { key: 'whistleDouble',         layer: 3 },
  'Mi-temps prolongation':          { key: 'whistleDouble',         layer: 3 },
  'Fin de prolongation':            { key: 'whistleDouble',         layer: 3 },
  'Fin du match':                   { key: 'whistleTriple',         layer: 3 },
  'Tirs aux buts marqués':          { key: 'shootoutScored',        layer: 3 },
  'Tirs aux buts manqués':          { key: 'shootoutMissed',        layer: 3 },
}

/**
 * Machine d'états Layer 2.
 * Un nouvel état ne remplace le précédent que si sa priorité est >= celle de l'état actuel.
 * Chaque état a ses propres durées de fadeIn/fadeOut.
 */
export const LAYER2_STATES = {
  idle:              { priority: 0, key: null,              fadeIn: 0,   fadeOut: 0.5 },
  dangerousAttack:   { priority: 2, key: 'dangerousAttack', fadeIn: 1.5, fadeOut: 1.5 },
  penalty:           { priority: 3, key: 'dangerousAttack', fadeIn: 0.5, fadeOut: 1.0 },
  var:               { priority: 4, key: 'dangerousAttack', fadeIn: 1.0, fadeOut: 2.0 },
  shootout:          { priority: 5, key: 'dangerousAttack', fadeIn: 0.5, fadeOut: 0.5 },
}

/**
 * Correspondance événement → état Layer 2.
 * Les événements non listés ici ne changent pas l'état Layer 2
 * (sauf s'ils résolvent un état de haute priorité).
 */
export const EVENT_TO_LAYER2_STATE = {
  'Attaque dangereuse':             'dangerousAttack',
  "Coup d'envoi":                   'dangerousAttack',
  "Coup d'envoi 2ème mi-temps":     'dangerousAttack',
  "Coup d'envoi prolongation":      'dangerousAttack',
  "Coup d'envoi 2ème MT prolongation": 'dangerousAttack',
  'Corner':                         'dangerousAttack',
  'Coup franc dangereux':           'dangerousAttack',
  'Temps additionnel':              'dangerousAttack',
  'Penalty':                        'penalty',
  'VAR':                            'var',
  "VAR (vérification d'un but)":    'var',
  'VAR (vérification penalty)':     'var',
  'VAR (Carton rouge)':             'var',
  'Pause avant tirs aux buts':      'shootout',
}

/**
 * Événements qui résolvent (terminent) un état Layer 2 de haute priorité.
 * Sans cela, un état VAR ou Penalty ne se termine que par un autre état de priorité >= .
 */
export const LAYER2_RESOLUTION_EVENTS = [
  'But',
  'Frappe cadrée',
  'Frappe non cadrée',
  'Tir non cadré',
  'Penalty manqué',
  'Possession',
  'Attaque',
  "Coup d'envoi",
  "Coup d'envoi 2ème mi-temps",
  "Coup d'envoi prolongation",
  "Coup d'envoi 2ème MT prolongation",
  'Mi-temps',
  'Mi-temps prolongation',
  'Fin de prolongation',
  'Fin du match',
]

/**
 * Cooldowns par famille de sons (en ms).
 * Le moteur ne rejouera pas un son de la même famille
 * si le dernier déclenchement date de moins de cooldownMs.
 */
export const SOUND_COOLDOWNS = {
  crowdOhh:            4000,
  crowdGasp:           4000,
  crowdApplause:       8000,
  crowdBoo:            8000,
  whistleShort:        1500,
  whistleDouble:       5000,
  whistleTriple:       10000,
  yellowCard:          3000,
  redCard:             5000,
  goal:                10000,
  shotOnTarget:        2000,
  shotOffTarget:       2000,
  penaltyAwarded:      5000,
  shootoutScored:      5000,
  shootoutMissed:      5000,
}

/**
 * Sound stacks — séquences multi-sons pour les moments clés.
 * Chaque entrée est un tableau de sons à jouer avec délai et duck.
 *   key: clé du son dans SOUND_FILES
 *   delay: délai en secondes après le début du stack
 *   volume: multiplicateur de volume
 *   duck: niveau de duck sur Layer 1 (0.0 = silence, null = pas de duck)
 */
export const SOUND_STACKS = {
  'Penalty': [
    { key: 'whistleShort', delay: 0,   volume: 0.9,  duck: 0.6 },
    { key: 'crowdGasp',    delay: 0.3, volume: 0.7,  duck: null },
  ],
  'Carton rouge': [
    { key: 'redCard',      delay: 0,   volume: 1.0,  duck: 0.5 },
    { key: 'crowdGasp',    delay: 0.3, volume: 0.8,  duck: null },
  ],
}

/**
 * Spatialisation stéréo par type de son.
 * Valeurs entre -1.0 (gauche) et +1.0 (droite), 0.0 = centre.
 * Utilisé en Phase 4 (StereoPannerNode), défini maintenant pour référence.
 */
/**
 * Fallbacks temporaires : si un fichier audio n'est pas encore fourni,
 * le moteur utilise un son existant à la place.
 * À supprimer au fur et à mesure que les vrais fichiers sont fournis.
 */
export const SOUND_FALLBACKS = {
  redCard:    'yellowCard',  // cartonrouge.webm absent → jaune.webm
}

export const PAN_MAP = {
  whistleShort:         0.0,
  whistleDouble:        0.0,
  whistleTriple:        0.0,
  crowdOhh:            -0.15,
  crowdGasp:            0.10,
  crowdApplause:        0.20,
  crowdBoo:            -0.20,
  goal:                 0.0,
  shotOnTarget:         0.0,
  shotOffTarget:        0.0,
  yellowCard:           0.0,
  redCard:              0.0,
  penaltyAwarded:       0.0,
}
